import { Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

import { FLOORPLAN_SRC } from '../../Types/Gnss';
import { getLayout as getGnssLayout } from '../../Utils/api/Gnss/api';

import type { Dashboard } from '../../Types/AuthServer';
import type { LayoutMap } from 'gnssgen/model';

export interface SpectrumMapTarget {
    deviceNumber: string;
    sensorSerial: string;
    targetViewIndex: number;
}

interface InfoscreenSpectrumMapViewProps {
    infoscreenLayout?: Dashboard['socials'];
    mapTargets: SpectrumMapTarget[];
    onNavigateToView: (viewIndex: number) => void;
}

interface LayoutCoord {
    xPct: number;
    yPct: number;
}

interface RenderedImageBox {
    height: number;
    left: number;
    top: number;
    width: number;
}

const getDirectionCandidateScore = (
    from: LayoutCoord,
    to: LayoutCoord,
    direction: 'left' | 'right' | 'up' | 'down',
): number | null => {
    const deltaX = to.xPct - from.xPct;
    const deltaY = to.yPct - from.yPct;

    if (direction === 'left' && deltaX >= 0) {
        return null;
    }
    if (direction === 'right' && deltaX <= 0) {
        return null;
    }
    if (direction === 'up' && deltaY >= 0) {
        return null;
    }
    if (direction === 'down' && deltaY <= 0) {
        return null;
    }

    const primaryDistance = direction === 'left' || direction === 'right' ? Math.abs(deltaX) : Math.abs(deltaY);
    const secondaryDistance = direction === 'left' || direction === 'right' ? Math.abs(deltaY) : Math.abs(deltaX);

    return primaryDistance * 100 + secondaryDistance;
};

export default function InfoscreenSpectrumMapView({
    infoscreenLayout,
    mapTargets,
    onNavigateToView,
}: Readonly<InfoscreenSpectrumMapViewProps>) {
    const [layoutMap, setLayoutMap] = useState<Record<string, LayoutCoord>>({});
    const [focusedMarkerIndex, setFocusedMarkerIndex] = useState<number>(0);
    const [renderedImageBox, setRenderedImageBox] = useState<RenderedImageBox>({
        height: 0,
        left: 0,
        top: 0,
        width: 0,
    });
    const rootRef = useRef<HTMLDivElement | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const markerRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const updateRenderedImageBox = () => {
        const rootElement = rootRef.current;
        const imageElement = imageRef.current;
        if (!rootElement || !imageElement) {
            return;
        }

        const containerWidth = rootElement.clientWidth;
        const containerHeight = rootElement.clientHeight;
        if (containerWidth <= 0 || containerHeight <= 0) {
            return;
        }

        const imageNaturalWidth = imageElement.naturalWidth;
        const imageNaturalHeight = imageElement.naturalHeight;
        if (imageNaturalWidth <= 0 || imageNaturalHeight <= 0) {
            setRenderedImageBox({
                height: containerHeight,
                left: 0,
                top: 0,
                width: containerWidth,
            });
            return;
        }

        const containerAspectRatio = containerWidth / containerHeight;
        const imageAspectRatio = imageNaturalWidth / imageNaturalHeight;

        if (imageAspectRatio > containerAspectRatio) {
            const renderedWidth = containerWidth;
            const renderedHeight = containerWidth / imageAspectRatio;
            setRenderedImageBox({
                height: renderedHeight,
                left: 0,
                top: (containerHeight - renderedHeight) / 2,
                width: renderedWidth,
            });
            return;
        }

        const renderedHeight = containerHeight;
        const renderedWidth = containerHeight * imageAspectRatio;
        setRenderedImageBox({
            height: renderedHeight,
            left: (containerWidth - renderedWidth) / 2,
            top: 0,
            width: renderedWidth,
        });
    };

    useEffect(() => {
        updateRenderedImageBox();

        const resizeObserver = new ResizeObserver(() => {
            updateRenderedImageBox();
        });

        const rootElement = rootRef.current;
        if (rootElement) {
            resizeObserver.observe(rootElement);
        }

        window.addEventListener('resize', updateRenderedImageBox);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateRenderedImageBox);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fetchLayout = async () => {
            try {
                const response = await getGnssLayout();
                if (!response.ok || cancelled) {
                    return;
                }

                const coords = (response.data as LayoutMap | undefined)?.coords;
                if (!coords || !Array.isArray(coords)) {
                    setLayoutMap({});
                    return;
                }

                const nextMap: Record<string, LayoutCoord> = {};
                coords.forEach((coord) => {
                    if (
                        typeof coord.device_id === 'string' &&
                        typeof coord.x_pct === 'number' &&
                        typeof coord.y_pct === 'number'
                    ) {
                        nextMap[coord.device_id] = { xPct: coord.x_pct, yPct: coord.y_pct };
                    }
                });

                setLayoutMap(nextMap);
            } catch (error) {
                console.error('Failed to fetch spectrum map layout:', error);
            }
        };

        void fetchLayout();

        return () => {
            cancelled = true;
        };
    }, []);

    const markers = useMemo(() => {
        return mapTargets
            .map((target) => {
                const coord = layoutMap[target.sensorSerial];
                if (!coord) {
                    return null;
                }
                return {
                    ...target,
                    xPct: coord.xPct,
                    yPct: coord.yPct,
                };
            })
            .filter((item): item is SpectrumMapTarget & LayoutCoord => item !== null);
    }, [layoutMap, mapTargets]);

    const clampedFocusedMarkerIndex = markers.length === 0 ? 0 : Math.min(focusedMarkerIndex, markers.length - 1);

    useEffect(() => {
        const activeMarkerElement = markerRefs.current[clampedFocusedMarkerIndex];
        if (activeMarkerElement) {
            activeMarkerElement.focus();
        }
    }, [clampedFocusedMarkerIndex, markers.length]);

    const moveMarkerFocus = (direction: 'left' | 'right' | 'up' | 'down') => {
        if (markers.length <= 1) {
            return;
        }

        const current = markers[clampedFocusedMarkerIndex] ?? markers[0];
        if (!current) {
            return;
        }

        let bestIndex = clampedFocusedMarkerIndex;
        let bestScore = Number.POSITIVE_INFINITY;

        markers.forEach((candidate, index) => {
            if (index === clampedFocusedMarkerIndex) {
                return;
            }

            const score = getDirectionCandidateScore(current, candidate, direction);
            if (score === null) {
                return;
            }

            if (score < bestScore) {
                bestScore = score;
                bestIndex = index;
            }
        });

        if (bestIndex !== clampedFocusedMarkerIndex) {
            setFocusedMarkerIndex(bestIndex);
        }
    };

    const handleMarkerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, markerIndex: number) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const marker = markers[markerIndex];
            if (marker) {
                onNavigateToView(marker.targetViewIndex);
            }
            return;
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            moveMarkerFocus('left');
            return;
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            moveMarkerFocus('right');
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveMarkerFocus('up');
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveMarkerFocus('down');
        }
    };

    return (
        <Box
            ref={rootRef}
            sx={{
                bgcolor: infoscreenLayout?.backgroundColor ?? '#000000',
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
            }}
        >
            <Box
                alt="Spectrum floorplan"
                component="img"
                onLoad={() => {
                    updateRenderedImageBox();
                }}
                ref={imageRef}
                src={FLOORPLAN_SRC}
                sx={{
                    display: 'block',
                    height: '100%',
                    objectFit: 'contain',
                    width: '100%',
                }}
            />
            {markers.map((marker, index) => {
                const isFocused = index === clampedFocusedMarkerIndex;
                return (
                    <Stack
                        key={`${marker.sensorSerial}-${marker.targetViewIndex.toString()}`}
                        spacing={0.2}
                        sx={{
                            alignItems: 'center',
                            left: `${(renderedImageBox.left + (renderedImageBox.width * marker.xPct) / 100).toString()}px`,
                            position: 'absolute',
                            top: `${(renderedImageBox.top + (renderedImageBox.height * marker.yPct) / 100).toString()}px`,
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <Button
                            onClick={() => {
                                setFocusedMarkerIndex(index);
                                onNavigateToView(marker.targetViewIndex);
                            }}
                            onFocus={() => {
                                setFocusedMarkerIndex(index);
                            }}
                            onKeyDown={(event) => {
                                handleMarkerKeyDown(event, index);
                            }}
                            ref={(element) => {
                                markerRefs.current[index] = element;
                            }}
                            sx={{
                                '&:hover': {
                                    bgcolor: '#33eeff',
                                },
                                bgcolor: '#00e5ff',
                                border: isFocused ? '2px solid #ffffff' : '1px solid #00e5ff',
                                borderRadius: '50%',
                                boxShadow: '0 0 10px rgba(0,229,255,0.65)',
                                color: '#001018',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                height: 32,
                                minWidth: 32,
                                p: 0,
                                width: 32,
                            }}
                            variant="contained"
                        >
                            {marker.deviceNumber}
                        </Button>
                    </Stack>
                );
            })}
            {mapTargets.length === 0 ? (
                <Stack
                    sx={{
                        alignItems: 'center',
                        inset: 0,
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        position: 'absolute',
                    }}
                >
                    <Typography
                        sx={{
                            color: '#ffffff',
                            opacity: 0.75,
                        }}
                        variant="h6"
                    >
                        No spectrum devices configured in this infoscreen.
                    </Typography>
                </Stack>
            ) : null}
        </Box>
    );
}
